/**
 * Format file size in bytes to a human-readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size (e.g., "2.5 MB")
 */
export function formatFileSize(bytes) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';

  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  const fileSize = (bytes / Math.pow(1024, i)).toFixed(2); // Keep two decimal places

  return fileSize + ' ' + sizes[i];
}

/**
 * Format a timestamp to a human-readable format using the user's locale
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted date and time
 */
export function formatTimestamp(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined) + ' ' + date.toLocaleTimeString(undefined);
}

/**
 * Format a timestamp to relative time (e.g., "2 days ago")
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Relative time string
 */
export function formatRelativeTime(timestamp) {
  const now = new Date();
  const inputDate = new Date(timestamp);
  const diffInSeconds = Math.floor((now - inputDate) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    return diffInMinutes + ' minute' + (diffInMinutes > 1 ? 's' : '') + ' ago';
  } else if (diffInSeconds < 86400) {
    const diffInHours = Math.floor(diffInSeconds / 3600);
    return diffInHours + ' hour' + (diffInHours > 1 ? 's' : '') + ' ago';
  } else if (diffInSeconds < 2592000) {
    const diffInDays = Math.floor(diffInSeconds / 86400);
    return diffInDays + ' day' + (diffInDays > 1 ? 's' : '') + ' ago';
  } else if (diffInSeconds < 31536000) {
    const diffInMonths = Math.floor(diffInSeconds / 2592000);
    return diffInMonths + ' month' + (diffInMonths > 1 ? 's' : '') + ' ago';
  } else {
    const diffInYears = Math.floor(diffInSeconds / 31536000);
    return diffInYears + ' year' + (diffInYears > 1 ? 's' : '') + ' ago';
  }
}

/**
 * Format a timestamp to show remaining time until expiry
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Remaining time string or "Expired"
 */
export function formatRemainingTime(timestamp) {
  const now = new Date();
  const expiryDate = new Date(timestamp);
  
  // If already expired
  if (now > expiryDate) {
    return 'Expired';
  }
  
  const diffInSeconds = Math.floor((expiryDate - now) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Less than a minute left';
  } else if (diffInSeconds < 3600) {
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    return diffInMinutes + ' minute' + (diffInMinutes > 1 ? 's' : '') + ' left';
  } else if (diffInSeconds < 86400) {
    const diffInHours = Math.floor(diffInSeconds / 3600);
    return diffInHours + ' hour' + (diffInHours > 1 ? 's' : '') + ' left';
  } else {
    const diffInDays = Math.floor(diffInSeconds / 86400);
    return diffInDays + ' day' + (diffInDays > 1 ? 's' : '') + ' left';
  }
}

/**
 * Check if a date is expiring soon (within 3 days)
 * @param {string} timestamp - ISO timestamp
 * @returns {boolean} True if expiring within 3 days
 */
export function isExpiringSoon(timestamp) {
  const now = new Date();
  const expiryDate = new Date(timestamp);
  const diffInDays = (expiryDate - now) / (1000 * 60 * 60 * 24);
  
  return diffInDays <= 3; // Consider "soon" if less than 3 days
} 